import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { auth, db, storage } from '../firebase';
import { collection, addDoc, getDocs, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useNavigate } from 'react-router-dom';

const emptyBatchEntry = () => ({ finalCoaNumber: '', batchNumber: '', qtyDispatched: '', qtyUnit: 'Kgs', coaFile: null, analysisResults: [{ test: '', specification: '', result: '' }] });

function CreateCoa({ sample = false }) {
    const navigate = useNavigate();

    // Sample COAs (pre-shipment / approvals) mirror regular COAs in a parallel collection and storage folder
    const recordsCollection = sample ? 'sample_coa_records' : 'coa_records';
    const storageFolder = sample ? 'sample_coas' : 'coas';
    const dashboardPath = sample ? '/sample-coa-dashboard' : '/coa-dashboard';

    // --- STATE MANAGEMENT ---
    const [products, setProducts] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [allCoaRecords, setAllCoaRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitLoading, setSubmitLoading] = useState(false);

    const [selectedProductId, setSelectedProductId] = useState('');
    const [dynamicFormState, setDynamicFormState] = useState({});
    const [mfgDate, setMfgDate] = useState('');
    const [dispatchDate, setDispatchDate] = useState('');

    // Single vs multiple batches: each entry becomes its own COA record
    const [batchMode, setBatchMode] = useState('single');
    const [numBatches, setNumBatches] = useState(2);
    const [batchEntries, setBatchEntries] = useState([emptyBatchEntry()]);
    const [suggestedNumbers, setSuggestedNumbers] = useState([]);

    const [coaFile, setCoaFile] = useState(null);
    // 'shared': one document attached to every batch record; 'individual': one upload per batch
    const [attachmentMode, setAttachmentMode] = useState('shared');
    const [purpose, setPurpose] = useState('Pre-shipment');
    const [courierName, setCourierName] = useState('');
    const [trackingNumber, setTrackingNumber] = useState('');

    const selectedProduct = useMemo(() => products.find(p => p.id === selectedProductId), [products, selectedProductId]);

    // Sample COAs are pre-shipment/approval documents, so invoice-related parameters don't apply
    const isHiddenAttribute = (attr) => sample && attr.toLowerCase().includes('invoice');

    // Financial year runs 1 April - 31 March; label e.g. "26-27" for FY 2026-27
    const getFinancialYearLabel = (date) => {
        const startYear = date.getMonth() >= 3 ? date.getFullYear() : date.getFullYear() - 1;
        return `${String(startYear).slice(-2)}-${String(startYear + 1).slice(-2)}`;
    };

    const entryCount = batchMode === 'single' ? 1 : Math.min(Math.max(parseInt(numBatches, 10) || 2, 2), 50);

    // Keep the entries array in sync with the requested batch count, preserving anything already typed
    useEffect(() => {
        setBatchEntries(prev => {
            if (prev.length === entryCount) return prev;
            const next = prev.slice(0, entryCount);
            while (next.length < entryCount) next.push(emptyBatchEntry());
            return next;
        });
    }, [entryCount]);

    const fetchData = useCallback(async () => {
        try {
            const [pSnap, cSnap] = await Promise.all([ getDocs(collection(db, "products")), getDocs(collection(db, "customers")) ]);
            setProducts(pSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setCustomers(cSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        } catch (error) {
            console.error("Critical error fetching master data:", error);
            alert("Could not load products/customers. Check your connection and Firestore rules, then reload.");
        }
        // Fetched separately so a rules problem on the records collection can't block the product dropdown
        try {
            const coaSnap = await getDocs(collection(db, recordsCollection));
            setAllCoaRecords(coaSnap.docs.map(doc => doc.data()));
        } catch (error) {
            console.error(`Error reading ${recordsCollection}:`, error);
            alert(`Could not read existing records from "${recordsCollection}" - check Firestore rules. COA number suggestions and duplicate checks will not work until this is fixed.`);
        }
        setLoading(false);
    }, [recordsCollection]);

    useEffect(() => { setLoading(true); fetchData(); }, [fetchData]);

    useEffect(() => {
        if (loading || !selectedProduct) {
            setSuggestedNumbers([]);
            setBatchEntries(prev => prev.map(entry => ({ ...entry, finalCoaNumber: '' })));
            return;
        }
        const customer = customers.find(c => c.id === dynamicFormState.customerId);
        let coaPrefix = 'COA';
        if (selectedProduct.rules) {
            for (const rule of selectedProduct.rules) {
                let match = true;
                const currentConditions = { "Company": customer?.name, ...dynamicFormState };
                for (const key in rule.conditions) {
                    if (rule.conditions[key] === '') continue; // "Any" in the rule editor
                    if (currentConditions[key] !== rule.conditions[key]) {
                        match = false;
                        break;
                    }
                }
                if (match && rule.result?.coaPrefix) {
                    coaPrefix = rule.result.coaPrefix;
                    break;
                }
            }
        }
        // Series resets to 001 every financial year; the FY label is part of the number.
        // Sample COAs carry an S marker (e.g. PHN-S-26-27-001) and run their own series.
        const fyLabel = getFinancialYearLabel(new Date());
        const seriesPrefix = `${coaPrefix}${sample ? '-S' : ''}-${fyLabel}-`;
        let maxNumber = 0;
        allCoaRecords.forEach(r => {
            if (r.coaNumber?.startsWith(seriesPrefix)) {
                const num = parseInt(r.coaNumber.slice(seriesPrefix.length), 10);
                if (!isNaN(num) && num > maxNumber) maxNumber = num;
            }
        });
        // One sequential number per batch entry
        const suggestions = Array.from({ length: entryCount }, (_, i) => `${seriesPrefix}${String(maxNumber + 1 + i).padStart(3, '0')}`);
        setSuggestedNumbers(suggestions);
        setBatchEntries(prev => prev.map((entry, i) => ({ ...entry, finalCoaNumber: suggestions[i] || '' })));
    }, [selectedProductId, dynamicFormState, loading, products, customers, allCoaRecords, selectedProduct, entryCount, sample]);

    const handleProductChange = (e) => {
        const productId = e.target.value;
        setSelectedProductId(productId);
        const product = products.find(p => p.id === productId);
        const initialDynamicState = { customerId: '' };
        if (product?.attributes) {
            product.attributes.forEach(attr => { if (!isHiddenAttribute(attr)) initialDynamicState[attr] = ''; });
        }
        setDynamicFormState(initialDynamicState);
    };
    const handleDynamicChange = (e) => setDynamicFormState(prev => ({ ...prev, [e.target.name]: e.target.value }));
    const handleFileChange = (e) => { if (e.target.files[0]) setCoaFile(e.target.files[0]); };

    const updateEntryField = (index, field, value) => setBatchEntries(prev => prev.map((entry, i) => i === index ? { ...entry, [field]: value } : entry));
    const handleAnalysisChange = (entryIndex, rowIndex, e) => setBatchEntries(prev => prev.map((entry, i) => i !== entryIndex ? entry : {
        ...entry,
        analysisResults: entry.analysisResults.map((row, r) => r === rowIndex ? { ...row, [e.target.name]: e.target.value } : row)
    }));
    const addAnalysisRow = (entryIndex) => setBatchEntries(prev => prev.map((entry, i) => i === entryIndex ? { ...entry, analysisResults: [...entry.analysisResults, { test: '', specification: '', result: '' }] } : entry));
    const removeAnalysisRow = (entryIndex, rowIndex) => setBatchEntries(prev => prev.map((entry, i) => i === entryIndex ? { ...entry, analysisResults: entry.analysisResults.filter((_, r) => r !== rowIndex) } : entry));

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!auth.currentUser) {
            alert("Authentication Error: No user is signed in. Please log out and log back in.");
            return;
        }

        const usesIndividualFiles = batchMode === 'multiple' && attachmentMode === 'individual';
        if (usesIndividualFiles) {
            if (batchEntries.some(entry => !entry.coaFile)) { alert("Please attach a COA file for every batch."); return; }
        } else {
            if (!coaFile) { alert("Please select a COA file to upload."); return; }
        }

        const finalNumbers = batchEntries.map(entry => entry.finalCoaNumber.trim());
        if (finalNumbers.some(n => !n)) { alert("Final COA Number is required for every batch."); return; }
        if (new Set(finalNumbers).size !== finalNumbers.length) { alert("Error: The same COA Number is entered for more than one batch."); return; }
        const alreadyExists = finalNumbers.find(n => allCoaRecords.some(record => record.coaNumber === n));
        if (alreadyExists) { alert(`Error: COA Number ${alreadyExists} already exists!`); return; }

        setSubmitLoading(true);
        try {
            // All uploads stay under coas/{batchFolder}/{fileName}, same path shape the storage rules already cover
            const uploadCoaFile = async (file, batchNumber, coaNumber) => {
                const filePath = `${storageFolder}/${batchNumber || 'general'}/${coaNumber}-${file.name}`;
                const fileRef = ref(storage, filePath);
                await uploadBytes(fileRef, file);
                const fileUrl = await getDownloadURL(fileRef);
                return { fileName: file.name, fileUrl, filePath };
            };

            let sharedFileInfo = null;
            if (!usesIndividualFiles) {
                sharedFileInfo = await uploadCoaFile(coaFile, batchEntries[0].batchNumber, finalNumbers[0]);
            }
            const selectedCustomer = customers.find(c => c.id === dynamicFormState.customerId);
            const financialYear = getFinancialYearLabel(new Date());

            // One COA record per batch entry, each pointing at its own or the shared document
            for (let i = 0; i < batchEntries.length; i++) {
                const entry = batchEntries[i];
                const fileInfo = usesIndividualFiles
                    ? await uploadCoaFile(entry.coaFile, entry.batchNumber, finalNumbers[i])
                    : sharedFileInfo;
                await addDoc(collection(db, recordsCollection), {
                    ...(sample ? { purpose, courierName: courierName.trim(), trackingNumber: trackingNumber.trim() } : {}),
                    productId: selectedProductId,
                    productName: selectedProduct?.productName,
                    coaNumber: finalNumbers[i],
                    financialYear,
                    batchNumber: entry.batchNumber,
                    mfgDate,
                    dispatchDate,
                    qtyDispatched: entry.qtyDispatched || '',
                    qtyUnit: entry.qtyDispatched ? (entry.qtyUnit || '') : '',
                    parameters: dynamicFormState,
                    customerName: selectedCustomer?.name,
                    customerCode: selectedCustomer?.companyCode,
                    analysisResults: entry.analysisResults,
                    fileName: fileInfo.fileName,
                    fileUrl: fileInfo.fileUrl,
                    filePath: fileInfo.filePath,
                    createdAt: serverTimestamp(),
                });
            }
            alert(batchEntries.length > 1 ? `${batchEntries.length} COA Records created successfully!` : 'COA Record created successfully!');
            navigate(dashboardPath);
        } catch (error) {
            console.error("Error creating COA record:", error);
            alert("Failed to create record. See console for details.");
        } finally {
            setSubmitLoading(false);
        }
    };

    if (loading) return <h2>Loading Master Data... Please wait.</h2>;

    return (
        <div>
            <h2>{sample ? 'Create New Sample COA Record (Pre-shipment / Approvals)' : 'Create New Certificate of Analysis Record'}</h2>
            <form onSubmit={handleSubmit}>
                <datalist id="qty-units">
                    <option value="Kgs" /><option value="MT" /><option value="Grams" /><option value="Ltrs" /><option value="Nos" /><option value="Drums" /><option value="Bags" />
                </datalist>
                <fieldset><legend>Step 1: Select Product</legend><select onChange={handleProductChange} value={selectedProductId} required><option value="">-- Choose a Product --</option>{products.map(p => <option key={p.id} value={p.id}>{p.productName}</option>)}</select></fieldset>
                {selectedProduct && (<>
                    <fieldset>
                        <legend>Step 2: Define Parameters for {selectedProduct.productName}</legend>
                        <div><label>Customer:</label><br/><select name="customerId" value={dynamicFormState.customerId || ''} onChange={handleDynamicChange} required><option value="">-- Select Customer --</option>{customers.map(c => <option key={c.id} value={c.id}>{[c.name, c.companyCode].filter(Boolean).join(' - ')}</option>)}</select></div>
                        {selectedProduct.attributes?.filter(attr => !isHiddenAttribute(attr)).map(attr => (<div key={attr} style={{marginTop: '10px'}}><label>{attr}:</label><br/><select name={attr} value={dynamicFormState[attr] || ''} onChange={handleDynamicChange} required><option value="">-- Select {attr} --</option>{selectedProduct.options[attr]?.map(opt => <option key={opt} value={opt}>{opt}</option>)}</select></div>))}
                        {sample && (
                            <div style={{marginTop: '10px'}}>
                                <label>Purpose:</label><br/>
                                <select value={purpose} onChange={e => setPurpose(e.target.value)} required>
                                    <option>Pre-shipment</option><option>Approval</option><option>R&D</option><option>Other</option>
                                </select>
                            </div>
                        )}
                        <div style={{marginTop: '10px'}}>
                            <label>Batch Selection:</label><br/>
                            <label><input type="radio" name="batchMode" value="single" checked={batchMode === 'single'} onChange={() => setBatchMode('single')} /> Single Batch</label>
                            <label style={{marginLeft: '20px'}}><input type="radio" name="batchMode" value="multiple" checked={batchMode === 'multiple'} onChange={() => setBatchMode('multiple')} /> Multiple Batches</label>
                        </div>
                        {batchMode === 'multiple' && (
                            <div style={{marginTop: '10px'}}>
                                <label>Number of Batches:</label><br/>
                                <input type="number" min="2" max="50" value={numBatches} onChange={e => setNumBatches(e.target.value)} required />
                            </div>
                        )}
                    </fieldset>
                    <fieldset>
                        <legend>Step 3: Enter Final Details</legend>
                        <div><label>Manufacturing Date:</label><br/><input type="month" value={mfgDate} onChange={e => setMfgDate(e.target.value)} required /></div>
                        <div><label>Date of Dispatch:</label><br/><input type="date" value={dispatchDate} onChange={e => setDispatchDate(e.target.value)} required /></div>
                        {sample && (<>
                            <div><label>Courier / Logistics Name (Optional):</label><br/><input type="text" value={courierName} onChange={e => setCourierName(e.target.value)} placeholder="e.g. DHL, Blue Dart" /></div>
                            <div><label>Tracking Number (Optional):</label><br/><input type="text" value={trackingNumber} onChange={e => setTrackingNumber(e.target.value)} placeholder="e.g. AWB / docket number" /></div>
                        </>)}
                    </fieldset>
                    {batchEntries.map((entry, index) => (
                        <fieldset key={index}>
                            <legend>{batchMode === 'multiple' ? `Batch ${index + 1} of ${batchEntries.length}` : 'Batch Details'}</legend>
                            <div><label>Suggested COA Number:</label><br/><input type="text" value={suggestedNumbers[index] || ''} disabled /></div>
                            <div><label>Final COA Number (Editable):</label><br/><input type="text" value={entry.finalCoaNumber} onChange={e => updateEntryField(index, 'finalCoaNumber', e.target.value)} required /></div>
                            <div><label>Production Batch Number:</label><br/><input type="text" value={entry.batchNumber} onChange={e => updateEntryField(index, 'batchNumber', e.target.value)} required /></div>
                            <div>
                                <label>Quantity Dispatched:</label><br/>
                                <input
                                    type="number"
                                    min="0"
                                    step="any"
                                    value={entry.qtyDispatched}
                                    onChange={e => updateEntryField(index, 'qtyDispatched', e.target.value)}
                                    placeholder="e.g. 1000"
                                    style={{width: '120px'}}
                                />
                                <input
                                    list="qty-units"
                                    value={entry.qtyUnit}
                                    onChange={e => updateEntryField(index, 'qtyUnit', e.target.value)}
                                    placeholder="Unit"
                                    style={{width: '90px', marginLeft: '10px'}}
                                />
                            </div>
                            {batchMode === 'multiple' && attachmentMode === 'individual' && (
                                <div style={{marginTop: '10px'}}>
                                    <label>Upload COA for this batch:</label><br/>
                                    <input type="file" onChange={e => updateEntryField(index, 'coaFile', e.target.files[0] || null)} required />
                                </div>
                            )}
                            <fieldset style={{marginTop: '10px'}}>
                                <legend>Analysis Results</legend>
                                {entry.analysisResults.map((row, rowIndex) => (
                                    <div key={rowIndex} style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                                        <input name="test" value={row.test} onChange={e => handleAnalysisChange(index, rowIndex, e)} placeholder="Test Parameter" required />
                                        <input name="specification" value={row.specification} onChange={e => handleAnalysisChange(index, rowIndex, e)} placeholder="Specification" required />
                                        <input name="result" value={row.result} onChange={e => handleAnalysisChange(index, rowIndex, e)} placeholder="Result" required />
                                        <button type="button" onClick={() => removeAnalysisRow(index, rowIndex)}>X</button>
                                    </div>
                                ))}
                                <button type="button" onClick={() => addAnalysisRow(index)}>+ Add Analysis Row</button>
                            </fieldset>
                        </fieldset>
                    ))}
                    <fieldset>
                        <legend>COA Document</legend>
                        {batchMode === 'multiple' && (
                            <div style={{marginBottom: '10px'}}>
                                <label>Attachment:</label><br/>
                                <label><input type="radio" name="attachmentMode" value="shared" checked={attachmentMode === 'shared'} onChange={() => setAttachmentMode('shared')} /> One document shared by all batches</label>
                                <label style={{marginLeft: '20px'}}><input type="radio" name="attachmentMode" value="individual" checked={attachmentMode === 'individual'} onChange={() => setAttachmentMode('individual')} /> Individual document per batch</label>
                            </div>
                        )}
                        {(batchMode === 'single' || attachmentMode === 'shared') ? (
                            <div><label>{batchMode === 'multiple' ? 'Upload Final COA (shared by all batches):' : 'Upload Final COA:'}</label><br/><input type="file" onChange={handleFileChange} required /></div>
                        ) : (
                            <p style={{margin: 0}}>Attach each batch's COA document in its batch section above.</p>
                        )}
                    </fieldset>
                    <button type="submit" disabled={submitLoading || loading} style={{marginTop: '20px', padding: '10px 20px'}}>{submitLoading ? 'Saving...' : (batchEntries.length > 1 ? `Save ${batchEntries.length} COA Records` : 'Save COA Record')}</button>
                </>)}
            </form>
        </div>
    );
}

export default CreateCoa;
